import type { DispatchTier } from '@sparkai/shared-types'

import { useInfiniteQuery } from '@tanstack/react-query'
import { sortBy } from 'lodash'

import { apiClient } from '../apiClient'

type PlatformPendingEngagementsListRequest = {
  limit: number
  cursor: number
  state?: 'open' | 'working'
  programIds: string[]
  orgProfileIds: string[]
  priority?: 'high' | 'low'
  sortDescending: boolean
}

type PendingEngagement = {
  engagementId: string
  clientId?: string | null
  state: 'open' | 'working' | 'closed'
  dateCreated: string
  dateClosed?: string
  timeRemaining: number
  beqUnderAgreement?: boolean
  dispatchTier: DispatchTier | null
  media: {
    location: string
    mediaId: string
  }[]
  tasks: {
    taskId: string
    engagementId: string
    state: 'open' | 'closed'
    operatorId: string | null
    operatorName: string | null
    avatarUrl: string | null
  }[]
  program: {
    programId: string
    displayName: string
    color: string
    iconType?: string
  }
}

type PlatformPendingEngagementsListResponse = {
  total: number
  pendingEngagements: PendingEngagement[]
  recentlyClosedTotal: number
  recentlyClosedEngagements: PendingEngagement[]
}

const fetchPendingEngagements = async (
  params: PlatformPendingEngagementsListRequest,
  pageParam: number
): Promise<PlatformPendingEngagementsListResponse> => {
  const { data } = await apiClient.get(`/int/v3/pending-engagements`, {
    params: { ...params, cursor: pageParam },
  })
  return data
}

export function useGetPendingEngagements(params: PlatformPendingEngagementsListRequest) {
  const query = useInfiniteQuery<PlatformPendingEngagementsListResponse>({
    queryKey: [
      'platform-pending-engagement',
      params.state,
      params.orgProfileIds,
      params.priority,
      params.programIds,
    ],
    queryFn: ({ pageParam = 0 }) => fetchPendingEngagements(params, pageParam as number),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      const totalFetched = allPages.reduce(
        (sum, page) => sum + page.pendingEngagements.length + page.recentlyClosedEngagements.length,
        0
      )
      const totalAvailable = lastPage.total + lastPage.recentlyClosedTotal
      if (totalFetched >= totalAvailable) return undefined
      return allPages.length * params.limit
    },
    refetchOnWindowFocus: false,
  })

  const { data } = query
  const hasPages = data?.pages && data?.pages.length > 0
  const totalPending = hasPages ? (data?.pages[0].total ?? 0) : 0
  const totalRecentlyClosed = hasPages ? (data?.pages[0].recentlyClosedTotal ?? 0) : 0
  const total = (totalPending ?? 0) + (totalRecentlyClosed ?? 0)

  const allClosedItems =
    data?.pages
      .flatMap((page) => page.recentlyClosedEngagements)
      .filter((item) => item !== null && item !== undefined) ?? []
  const allPendingItems =
    data?.pages
      .flatMap((page) => page.pendingEngagements)
      .filter((item) => item !== null && item !== undefined) ?? []

  const merged = [...allPendingItems, ...allClosedItems]

  return {
    ...query,
    total,
    closedItems: allClosedItems,
    items: sortBy(merged, (item) => new Date(item.dateCreated).getTime()).reverse(),
  }
}
